/**
 * CanvasOverlays.tsx
 * Composant pour gérer tous les overlays du canvas (Rulers, NavigationBar, Minimap, etc.)
 * Extrait de CanvasArea.tsx pour améliorer la maintenabilité
 */
import React from 'react';
import Konva from 'konva';
import { EnhancedRulers } from './EnhancedRulers';
import { CanvasNavigationBar } from './CanvasNavigationBar';
import { CanvasMinimap } from './CanvasMinimap';
import { FloatingActionMenu } from './FloatingActionMenu';

interface CanvasOverlaysProps {
  templateWidth: number;
  templateHeight: number;
  zoomLevel: number;
  stageRef: React.RefObject<Konva.Stage | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  stagePosition: { x: number; y: number };
  onStagePositionChange: (pos: { x: number; y: number }) => void;
  // PR-09: Utiliser selectionIds et selectionBounds au lieu de selectedShape
  selectionIds: string[];
  selectionBounds: { x: number; y: number; width: number; height: number } | null;
}

/**
 * Composant pour gérer tous les overlays du canvas
 */
export const CanvasOverlays: React.FC<CanvasOverlaysProps> = ({
  templateWidth,
  templateHeight,
  zoomLevel,
  stageRef,
  containerRef,
  stagePosition,
  onStagePositionChange,
  selectionIds,
  selectionBounds,
}) => {
  return (
    <>
      {/* Règles améliorées avec unités et feedback visuel */}
      <EnhancedRulers
        canvasWidth={templateWidth}
        canvasHeight={templateHeight}
        zoomLevel={zoomLevel}
        canvasPosition={{ x: 50, y: 50 }}
        stageRef={stageRef}
      />

      {/* Barre d'arrangement multi-sélection (alignement, distribution, rotation,
          z-order…). Affichée UNIQUEMENT à partir de 2 éléments sélectionnés : c'est
          là que ces opérations ont du sens, et ça évite le chevauchement avec la
          SelectionFloatingToolbar (qui ne s'affiche qu'en sélection unique). */}
      {selectionBounds && selectionIds.length >= 2 && (
        <FloatingActionMenu
          selectionBounds={selectionBounds}
          selectionIds={selectionIds}
          zoomLevel={zoomLevel}
          canvasOffset={{ x: 50, y: 50 }}
          stagePosition={stagePosition}
        />
      )}

      {/* Barre de navigation du canvas */}
      <CanvasNavigationBar
        stageRef={stageRef}
        containerRef={containerRef}
        onStagePositionChange={onStagePositionChange}
      />

      {/* Mini-map (vue d'ensemble) */}
      <CanvasMinimap
        stageRef={stageRef}
        containerRef={containerRef}
        onViewportClick={(_x, _y) => {
          // Optionnel: Action supplémentaire lors du clic
        }}
      />
    </>
  );
};
